// @givmo/passkeys — the passkey setup offer (A1; AS-WEB11; D-AS-21's approved frame, wording per
// D-AS-29 and Board's web note W1).
//
// The card a host shows when a person who will need to confirm it's them has nothing to confirm
// with: Board's director right after joining (`default`), right after pressing Sign (`from-sign`),
// right after pressing Compliance's Authorize and send (`from-send`, CCM-R1-SIGN-PASSKEY), or right
// after Save on Compliance's sign-in check card, making the check less strict (`from-level`,
// AS-PWL-2e).
// Its heading and line are the host's to place (`passkeySetupOfferWords`); this is the card beneath
// them, exactly as the frame draws it:
//   - "Set up Touch ID" (the device's own word, `passkey-device.ts`), creating a passkey through the
//     "Confirm it's you" gate (`useAddPasskey`);
//   - "Use an authenticator app instead": ONLY for an account the web offers one to (Google, Apple or
//     Microsoft sign-ins while authenticator apps are served, D-AS-26 N2), and only where the host
//     hands over its authenticator primitives (`authenticator`, AS-WEB14): the offer runs the setup
//     itself and settles `created` once the factor exists. There is no other door, so no host can
//     draw it for an account N2 excludes (cut 1003c's review). Never drawn where it could not work;
//   - "Not now", with its line: "You can still read and reply. We'll ask again when you first sign."
//     by default, "Nothing is signed until you set one up." opened from Sign, "Setting one up
//     doesn't send anything." opened from Send, and "Setting one up doesn't change this setting."
//     (approved, D-CCM-88) opened from the sign-in check card. The line is the ONLY thing a variant
//     changes.
//
// STATELESS (Board's contract, answer 4): it remembers nothing about having been offered. It settles
// once — `created` after the passkey is stored (or, by the alternative, the authenticator app),
// `declined` on "Not now", `unavailable` when this
// browser or this environment cannot create one (no WebAuthn, or the surface is dark). A dismissed
// browser prompt or sheet settles nothing: the person is still looking at the offer.
//
// A FIRST PASSKEY ON A SIGN-IN OLDER THAN RULE 1'S WINDOW (AS-PWL-2e; Compliance's review-76, P1):
// the add ends `sign_in_needed`, and the offer says the refusal's own words, never "That didn't
// work. Try again.", which a retry could not make true. Where the host supplies `signInAgain`, a
// "Sign in again" button takes the set-up button's place: the console's sign-out (the suite session
// too) and back to this page once signed in, where a fresh sign-in, a new emailed code included,
// adds the passkey. It settles nothing, so "Not now" still works.
//
// UNDER A SIGN-IN LOCK (AS-WEB23; frame E3, DRAFT words; `sign-in-lock.ts`): no setup at all, neither
// the passkey nor the authenticator alternative, since the lock refuses what either would make. A
// host that places the offer's words places the lock's instead (`signInLockOfferWords`: Board's
// shell, `wordsPlaced`), and the card beneath is one "OK"; a host that places none (Compliance's
// acts) gets the Sign-in & security card's line above the "OK". "OK" settles `unavailable`: nothing
// can be set up here. The lock is the account's security read, keyed by `uid` (or the
// authenticator's); a press on an offer drawn before the lock bound re-reads it first and starts
// nothing (`useAddPasskey`, `useAuthenticatorSetup`).

import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, CardBody, ErrorMessage, Icon, cn, testIdProp } from '@givmo/design-system';
import { isPasskeyRegistrationSupported } from '@givmo/auth';
import { Fingerprint, ScanFace } from 'lucide-react';
import { useOptionalStepUpHost } from '@givmo/step-up';
import { servedData, useMySecurity } from './account-factors';
import { authenticatorAppOffered } from './authenticator-eligibility';
import { useAuthenticatorSetup, type AuthenticatorControls } from './authenticator-setup';
import { PASSKEY_DEVICE_WORDS, detectPasskeyDevice, type PasskeyDevice } from './passkey-device';
import {
  SIGN_IN_LOCK_WORDS,
  signInLockCardLine,
  useSignInLock,
  type SignInLock,
} from './sign-in-lock';
import { PASSKEY_LIMIT_LINE, SIGN_IN_AGAIN_LABEL, useAddPasskey } from './use-add-passkey';

export type PasskeySetupOutcome = 'created' | 'declined' | 'unavailable';

export interface PasskeySetupOfferProps {
  /**
   * `from-sign` carries Board's from-Sign line under "Not now"; `from-send`, Compliance's from-Send
   * line (CCM-R1-SIGN-PASSKEY); `from-level`, the line for Compliance's sign-in check card, opened
   * from its Save (AS-PWL-2e). Nothing else differs between variants.
   */
  variant: 'default' | 'from-sign' | 'from-send' | 'from-level';
  /** Called once, with how the offer ended. */
  onSettled: (outcome: PasskeySetupOutcome) => void;
  /**
   * The console's authenticator primitives (AS-WEB14): with them, for an account the web offers an
   * authenticator to, the offer draws the alternative and runs the setup itself.
   */
  authenticator?: AuthenticatorControls;
  /** The device whose words the offer speaks; detected from the browser when absent. */
  device?: PasskeyDevice;
  /**
   * The signed-in account (AS-WEB23), whose security read says whether a sign-in lock binds it.
   * Absent, `authenticator.uid`; with neither, the offer can't tell and offers setup as before.
   */
  uid?: string | null;
  /**
   * The host places the offer's title and line above it, as Board's shell does
   * (`passkeySetupOfferWords`, or `signInLockOfferWords` under a lock). Under a lock an offer whose
   * host places none says why itself.
   */
  wordsPlaced?: boolean;
}

const NOT_NOW_LINE: Record<PasskeySetupOfferProps['variant'], string> = {
  // SC2, APPROVED (D-CCM-88, Jonathan 2026-10-14 20:19 PDT, "aprove recs"; Account Security's
  // D-AS-51): under "Not now" on the offer opened from the sign-in check card's Save (AS-PWL-2e, in
  // Compliance's words on D-CCM-36's W2 ruling). It says what setting one up does, since Save
  // doesn't run again by itself.
  'from-level': 'Setting one up doesn’t change this setting.',
  default: 'You can still read and reply. We’ll ask again when you first sign.',
  'from-sign': 'Nothing is signed until you set one up.',
  // Compliance's W2 (approved, D-CCM-36, Jonathan 2026-10-06, on cut charity-compliance-1006-4's
  // render): under "Not now" on the offer opened from Authorize and send. It says what setting one
  // up does, since Send doesn't run again by itself, as Sign does after its setup.
  'from-send': 'Setting one up doesn’t send anything.',
};

/** The one failure line the offer has, borrowed from the sheet's approved words (B3d). */
const FAILED_LINE = 'That didn’t work. Try again.';

export function PasskeySetupOffer({ uid, wordsPlaced = false, ...offer }: PasskeySetupOfferProps) {
  const account = uid ?? offer.authenticator?.uid ?? null;
  const lock = useSignInLock(account);
  if (lock.active) {
    return <LockedSetupOffer lock={lock} wordsPlaced={wordsPlaced} onSettled={offer.onSettled} />;
  }
  return <SetupOffer {...offer} account={account} />;
}

/** A1 under a sign-in lock (frame E3): nothing to set up, and one way on. */
function LockedSetupOffer({
  lock,
  wordsPlaced,
  onSettled,
}: {
  lock: SignInLock;
  wordsPlaced: boolean;
  onSettled: (outcome: PasskeySetupOutcome) => void;
}) {
  // It settles ONCE, as the offer does.
  const settledRef = useRef(false);
  return (
    <div
      className="flex w-full max-w-[440px] flex-col items-start gap-16"
      {...testIdProp('passkey-setup-offer-locked')}
    >
      {!wordsPlaced && (
        <p
          className="rounded-lg border border-border-subtle bg-surface-sunken px-16 py-12 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
          {...testIdProp('passkey-setup-offer-locked-line')}
        >
          {signInLockCardLine(lock)}
        </p>
      )}
      <Button
        appearance="saas"
        tone="secondary"
        size="lg"
        onClick={() => {
          if (settledRef.current) return;
          settledRef.current = true;
          onSettled('unavailable');
        }}
        testId="passkey-setup-offer-ok"
      >
        {SIGN_IN_LOCK_WORDS.ok}
      </Button>
    </div>
  );
}

function SetupOffer({
  variant,
  onSettled,
  authenticator,
  device,
  account,
}: Omit<PasskeySetupOfferProps, 'uid' | 'wordsPlaced'> & { account: string | null }) {
  const resolvedDevice = device ?? detectPasskeyDevice();
  const words = PASSKEY_DEVICE_WORDS[resolvedDevice];
  const { add, pending, limitReached, signInNeeded, dialog } = useAddPasskey(
    'passkey-offer-step-up',
    { uid: account },
  );
  const host = useOptionalStepUpHost();
  const securityRead = useMySecurity(authenticator?.uid ?? null);
  const security = servedData(securityRead);
  const setup = useAuthenticatorSetup(authenticator, {
    testIdPrefix: 'passkey-offer-step-up',
    device: resolvedDevice,
  });
  const offersAuthenticator =
    authenticator !== undefined && authenticatorAppOffered(host?.waysIn ?? [], security);
  const [failed, setFailed] = useState(false);
  const supported = isPasskeyRegistrationSupported();
  // Only a new sign-in adds the first passkey now: the host's way there, where it has one.
  const signInAgain = signInNeeded !== null ? host?.signInAgain : undefined;
  // It settles ONCE, whatever re-renders a host's inline callback causes.
  const settledRef = useRef(false);
  const settle = useCallback(
    (outcome: PasskeySetupOutcome) => {
      if (settledRef.current) return;
      settledRef.current = true;
      onSettled(outcome);
    },
    [onSettled],
  );
  // The alternative, for an account offered one: the setup runs here and settles once the factor exists.
  const chooseAuthenticator = offersAuthenticator
    ? () => {
        setFailed(false);
        void setup.start().then((outcome) => {
          if (outcome === 'enrolled') settle('created');
          else if (outcome === 'failed') setFailed(true);
        });
      }
    : undefined;
  const hasAlternative = chooseAuthenticator !== undefined;
  // Whether the alternative is decided: none to decide, or the account read answered.
  const alternativeKnown = authenticator === undefined || securityRead.isFetched;

  // A browser that cannot create a passkey has nothing to offer here (the authenticator alternative
  // aside): settle at once rather than draw a button that cannot work — but only once the account
  // read has said whether the alternative exists, or an account offered one would be settled away.
  useEffect(() => {
    if (!supported && alternativeKnown && !hasAlternative) settle('unavailable');
  }, [supported, alternativeKnown, hasAlternative, settle]);

  const setUp = async () => {
    setFailed(false);
    const outcome = await add();
    if (outcome === 'created' || outcome === 'unavailable') settle(outcome);
    else if (outcome === 'failed') setFailed(true);
  };

  return (
    <Card className="w-full max-w-[440px]" testId="passkey-setup-offer">
      <CardBody className="flex flex-col gap-12">
        {failed && (
          <ErrorMessage className="pt-0" testId="passkey-setup-offer-failed">
            {limitReached ? PASSKEY_LIMIT_LINE : FAILED_LINE}
          </ErrorMessage>
        )}
        {signInNeeded !== null && (
          <ErrorMessage className="pt-0" testId="passkey-setup-offer-sign-in-needed">
            {signInNeeded}
          </ErrorMessage>
        )}
        {signInAgain !== undefined && (
          <Button
            appearance="saas"
            tone="primary"
            size="lg"
            fullWidth
            onClick={signInAgain}
            testId="passkey-setup-offer-sign-in-again"
          >
            {SIGN_IN_AGAIN_LABEL}
          </Button>
        )}
        {supported && signInAgain === undefined && (
          <Button
            appearance="saas"
            tone="primary"
            size="lg"
            fullWidth
            loading={pending}
            leadingIcon={<Icon icon={words.face ? ScanFace : Fingerprint} size={18} aria-hidden />}
            onClick={() => void setUp()}
            testId="passkey-setup-offer-set-up"
          >
            {words.setUp}
          </Button>
        )}
        {chooseAuthenticator !== undefined && (
          <Button
            appearance="saas"
            tone="secondary"
            size="lg"
            fullWidth
            disabled={pending}
            loading={setup.pending}
            onClick={chooseAuthenticator}
            testId="passkey-setup-offer-authenticator"
          >
            Use an authenticator app instead
          </Button>
        )}
        <div className="mt-4 flex flex-col items-center gap-4 border-t border-border-subtle pt-16">
          <button
            type="button"
            disabled={pending}
            onClick={() => settle('declined')}
            className={cn(
              'font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline disabled:opacity-60',
            )}
            {...testIdProp('passkey-setup-offer-not-now')}
          >
            Not now
          </button>
          <p
            className="text-center font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
            {...testIdProp('passkey-setup-offer-not-now-line')}
          >
            {NOT_NOW_LINE[variant]}
          </p>
        </div>
      </CardBody>
      {dialog}
      {setup.dialog}
    </Card>
  );
}
